import { executeSQL } from '@carto/react-api';
import { addLayer, addSource, removeLayer } from '@carto/react-redux';
import { useQuery } from '@tanstack/react-query';
import CompareCountiesSidePanel from 'components/common/CompareCountiesSidePanel';
import CompareSimilarTractsSidePanel from 'components/common/CompareSimilarTractsSidePanel';
import CompareStatesSidePanel from 'components/common/CompareStatesSidePanel';
import CompareTractsSidePanel from 'components/common/CompareTractsSidePanel';
import CountySidePanel from 'components/common/CountySidePanel';
import FilterBox from 'components/common/FilterBox';
import ScoreIndicator from 'components/common/ScoreIndicator';
import SimilarTractsSidePanel from 'components/common/SimilarTractsSidePanel';
import StateSidePanel from 'components/common/StateSidePanel';
import TractSidePanel from 'components/common/TractSidePanel';
import { COUNTY_BROADBAND_LAYER_ID } from 'components/layers/CountyBroadbandLayer';
import { COUNTY_SOCIOECONOMIC_LAYER_ID } from 'components/layers/CountySocioeconomicLayer';
import { STATE_BROADBAND_LAYER_ID } from 'components/layers/StateBroadbandLayer';
import { STATE_SOCIOECONOMIC_LAYER_ID } from 'components/layers/StateSocioeconomicLayer';
import { TRACT_BROADBAND_LAYER_ID } from 'components/layers/TractBroadbandLayer';
import { TRACT_SOCIOECONOMIC_LAYER_ID } from 'components/layers/TractSocioeconomicLayer';
import countiesByCountyIdsSource from 'data/sources/countiesByCountyIdsSource';
import countyBroadbandSource from 'data/sources/countyBroadbandSource';
import countySocioeconomicSource from 'data/sources/countySocioeconomicSource';
import countyVisualizationsSource from 'data/sources/countyVisualizationsSource';
import searchboxAutosuggestSource from 'data/sources/searchboxAutosuggestSource';
import similarTractsSource from 'data/sources/similarTractsSource';
import socioeconomicFeaturesSource from 'data/sources/socioeconomicFeaturesSource';
import stateBroadbandSource from 'data/sources/stateBroadbandSource';
import stateSocioeconomicSource from 'data/sources/stateSocioeconomicSource';
import stateVisualizationsSource from 'data/sources/stateVisualizationsSource';
import statesByStatenamesSource from 'data/sources/statesByStatenamesSource';
import tractBroadbandSource from 'data/sources/tractBroadbandSource';
import tractPredictionsSource from 'data/sources/tractPredictionsSource';
import tractSocioeconomicSource from 'data/sources/tractSocioeconomicSource';
import tractVisualizationsSource from 'data/sources/tractVisualizationsSource';
import tractsByTractcodesSource from 'data/sources/tractsByTractcodesSource';
import { lazy, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { generateCountyId } from 'utils/countyId';
import {
  COUNTY_PANEL_TYPES,
  STATE_PANEL_TYPES,
  TRACT_PANEL_TYPES,
} from 'utils/panel-types';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';

import LazyLoadComponent from 'components/common/LazyLoadComponent';
import SidePanel from 'components/common/SidePanel';
import { findFeatureLabel } from 'utils/featureLabel';

const MapContainer = lazy(() =>
  import(/* webpackChunkName: 'map-container' */ 'components/views/main/MapContainer')
);

export default function Main() {
  const dispatch = useDispatch();
  const zoomLevel = useSelector((state) => Math.floor(state.carto.viewState.zoom));

  const defaultSocioconomicLayerSearchParam = () => {
    const value = searchParams.get('socioeconomic-layer');

    return value !== null ? value === 'true' : true;
  };

  const defaultSBroadbandLayerSearchParam = () => {
    const value = searchParams.get('broadband-layer');

    return value || 'broadband-score';
  };

  const [features, setFeatures] = useState(null);
  const credentials = useSelector((state) => state.carto.credentials);
  const [searchParams, setSearchParams] = useSearchParams();
  const [socioeconomicYearsChartData, _setSocioeconomicYearsChartData] = useState(null);
  const [socioeconomicYearsChartLabels, _setSocioeconomicYearsChartLabels] =
    useState(null);
  const [socioeconomicFactorParsed, _setSocioeconomicFactorParsed] = useState(null);
  const [similarTracts, setSimilarTracts] = useState(null);
  const [isSocioeconomicFactorsLayerActive, setIsSocioeconomicFactorsLayerActive] =
    useState(defaultSocioconomicLayerSearchParam());
  const [activeBroadbandLayer, setActiveBroadbandLayer] = useState(
    defaultSBroadbandLayerSearchParam()
  );
  const [activeTract, setActiveTract] = useState(null);
  const [activeCounty, setActiveCounty] = useState(null);
  const [activeState, setActiveState] = useState(null);
  const [tractsSearchQuery, setTractsSearchQuery] = useState('');
  const [statesSearchQuery, setStatesSearchQuery] = useState('');
  const [countiesSearchQuery, setCountiesSearchQuery] = useState('');
  const [activeCompareTract, setActiveCompareTract] = useState(null);
  const [activeCompareCounty, setActiveCompareCounty] = useState(null);
  const [activeCompareState, setActiveCompareState] = useState(null);
  const [activeCompareSimilarTract, setActiveCompareSimilarTract] = useState(null);
  const [compareTractsItems, setCompareTractsItems] = useState([]);
  const [compareCountiesItems, setCompareCountiesItems] = useState([]);
  const [compareStatesItems, setCompareStatesItems] = useState([]);
  const [tractSocioeconomicBroadbandData, setTractSocioeconomicBroadbandData] =
    useState();
  const [stateSocioeconomicYearsData, setStateSocioeconomicYearsData] = useState();
  const [countySocioeconomicYearsData, setCountySocioeconomicYearsData] = useState();
  const [
    tractSocioeconomicBroadbandPredictionsData,
    setTractSocioeconomicBroadbandPredictionsData,
  ] = useState();
  const [
    tractSocioeconomicBroadbandDataCompareSimilarTractA,
    setTractSocioeconomicBroadbandDataCompareSimilarTractA,
  ] = useState();
  const [
    tractSocioeconomicBroadbandPredictionsDataCompareSimilarTractA,
    setTractSocioeconomicBroadbandPredictionsDataCompareSimilarTractA,
  ] = useState();
  const [
    tractSocioeconomicBroadbandDataCompareSimilarTractB,
    setTractSocioeconomicBroadbandDataCompareSimilarTractB,
  ] = useState();
  const [
    tractSocioeconomicBroadbandPredictionsDataCompareSimilarTractB,
    setTractSocioeconomicBroadbandPredictionsDataCompareSimilarTractB,
  ] = useState();
  const [tractSocioeconomicYearsData, setTractSocioeconomicYearsData] = useState();

  let year = searchParams.get('year');

  const queryFnTracts = useCallback(
    async ({ signal }) => {
      let searchAutoSuggestSource = searchboxAutosuggestSource(
        tractsSearchQuery,
        'tract',
        activeTract?.tractcode || 0,
        year
      );

      return await executeSQL({
        credentials: credentials,
        query: searchAutoSuggestSource.data,
        connection: searchAutoSuggestSource.connection,
        opts: {
          abortController: {
            signal,
          },
        },
      });
    },
    [activeTract?.tractcode, credentials, tractsSearchQuery, year]
  );

  let setYear = function (value) {
    setSearchParams((params) => {
      params.set('year', value);

      return params;
    });
  };

  let setSocioeconomicFactor = function (value) {
    setSearchParams((params) => {
      params.set('socioeconomic-factor', value);

      return params;
    });
  };

  let socioeconomicFactor = searchParams.get('socioeconomic-factor');

  useEffect(() => {
    if (searchParams.get('year') === null) {
      setSearchParams((params) => {
        params.set('year', '2021');

        return params;
      });

      // year = searchParams.get('year');
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('socioeconomic-factor') === null) {
      setSearchParams((params) => {
        params.set('socioeconomic-factor', 'overall_socioeconomic');

        return params;
      });

      // socioeconomicFactor = searchParams.get('socioeconomic-factor');
    }
  }, [searchParams, setSearchParams]);

  const { data: searchResultsTracts, isPending: isSearchResultsTractsLoading } = useQuery(
    {
      queryKey: ['searchSuggestionsTracts', tractsSearchQuery],
      queryFn: queryFnTracts,
    }
  );

  const queryFnStates = useCallback(
    async ({ signal }) => {
      let searchAutoSuggestSource = searchboxAutosuggestSource(
        statesSearchQuery,
        'state',
        activeState?.statename || 0,
        year
      );

      return await executeSQL({
        credentials: credentials,
        query: searchAutoSuggestSource.data,
        connection: searchAutoSuggestSource.connection,
        opts: {
          abortController: {
            signal,
          },
        },
      });
    },
    [activeState?.statename, credentials, statesSearchQuery, year]
  );

  const { data: searchResultsStates, isPending: isSearchResultsStatesLoading } = useQuery(
    {
      queryKey: ['searchSuggestionsStates', statesSearchQuery],
      queryFn: queryFnStates,
    }
  );

  const queryFnCounties = useCallback(
    async ({ signal }) => {
      let searchAutoSuggestSource = searchboxAutosuggestSource(
        countiesSearchQuery,
        'county',
        activeCounty?.countyname || 0,
        year
      );

      return await executeSQL({
        credentials: credentials,
        query: searchAutoSuggestSource.data,
        connection: searchAutoSuggestSource.connection,
        opts: {
          abortController: {
            signal,
          },
        },
      });
    },
    [activeCounty?.countyname, credentials, countiesSearchQuery, year]
  );

  const { data: searchResultsCounties, isPending: isSearchResultsCountiesLoading } =
    useQuery({
      queryKey: ['searchSuggestionsCounties', countiesSearchQuery],
      queryFn: queryFnCounties,
    });

  const broadbandLayers = [
    {
      id: 'broadband-score',
      label: 'Broadband score (nr)',
    },
    {
      id: 'broadband-growth-score',
      label: 'Broadband growth score (%)',
    },
  ];

  let toggleSocioeconomicFactorsLayerActiveSearchParam = function (value) {
    setSearchParams((params) => {
      params.set('socioeconomic-layer', value);

      return params;
    });
  };

  let activateBroadbandLayerSearchParam = function (value) {
    setSearchParams((params) => {
      params.set('broadband-layer', value);

      return params;
    });
  };

  let updateSelectedTract = useCallback(
    function (selected) {
      if (selected?.tractcode === undefined) {
        setSearchParams((params) => {
          params.delete('active-compare-tract');

          return params;
        });
      } else {
        setSearchParams((params) => {
          params.set('active-compare-tract', selected?.tractcode);

          return params;
        });
      }

      setActiveCompareTract(selected);
    },
    [setSearchParams]
  );

  let updateSelectedCounty = useCallback(
    function (selected) {
      if (selected?.countyname === undefined) {
        setSearchParams((params) => {
          params.delete('active-compare-county');

          return params;
        });
      } else {
        setSearchParams((params) => {
          params.set(
            'active-compare-county',
            generateCountyId(selected?.countyname, selected?.statename)
          );

          return params;
        });
      }

      setActiveCompareCounty(selected);
    },
    [setSearchParams]
  );

  let updateSelectedState = useCallback(
    function (selected) {
      if (selected?.statename === undefined) {
        setSearchParams((params) => {
          params.delete('active-compare-state');

          return params;
        });
      } else {
        setSearchParams((params) => {
          params.set('active-compare-state', selected?.statename);

          return params;
        });
      }

      setActiveCompareState(selected);
    },
    [setSearchParams]
  );

  let updateSelectedCompareSimilarTract = useCallback(
    function (selected) {
      if (selected?.tractcode === undefined) {
        setSearchParams((params) => {
          params.delete('active-compare-similar-tract');

          return params;
        });
      } else {
        setSearchParams((params) => {
          params.set('active-compare-similar-tract', selected?.tractcode);

          return params;
        });
      }

      setActiveCompareSimilarTract(selected);
    },
    [setSearchParams]
  );

  useEffect(() => {
    const value = searchParams.get('socioeconomic-layer');
    const parsedValue = value !== null ? value === 'true' : true;

    if (parsedValue !== isSocioeconomicFactorsLayerActive) {
      setIsSocioeconomicFactorsLayerActive(parsedValue);
    }
  }, [isSocioeconomicFactorsLayerActive, searchParams]);

  useEffect(() => {
    const value = searchParams.get('broadband-layer');
    const parsedValue = value || 'broadband-score';

    if (parsedValue !== activeBroadbandLayer) {
      setActiveBroadbandLayer(parsedValue);
    }
  }, [activeBroadbandLayer, searchParams]);

  // Tract broadband layer.
  useEffect(() => {
    let currentTractBroadbandSource = tractBroadbandSource(year);

    if (isLayerVisible(LAYER_TYPE.TRACT, zoomLevel) === false) {
      dispatch(removeLayer(TRACT_BROADBAND_LAYER_ID));
      // dispatch(removeSource(currentTractBroadbandSource.id));
    } else {
      dispatch(addSource(currentTractBroadbandSource));

      dispatch(
        addLayer({
          id: TRACT_BROADBAND_LAYER_ID,
          source: currentTractBroadbandSource.id,
          layerAttributes: {
            year,
          },
        })
      );
    }

    return () => {
      dispatch(removeLayer(TRACT_BROADBAND_LAYER_ID));
      // dispatch(removeSource(currentTractBroadbandSource.id));
    };
  }, [year, dispatch, zoomLevel]);

  // County broadband layer.
  useEffect(() => {
    let currentCountyBroadbandSource = countyBroadbandSource(year);

    if (isLayerVisible(LAYER_TYPE.COUNTY, zoomLevel) === false) {
      dispatch(removeLayer(COUNTY_BROADBAND_LAYER_ID));
      // dispatch(removeSource(currentCountyBroadbandSource.id));
    } else {
      dispatch(addSource(currentCountyBroadbandSource));

      dispatch(
        addLayer({
          id: COUNTY_BROADBAND_LAYER_ID,
          source: currentCountyBroadbandSource.id,
          layerAttributes: {
            year,
          },
        })
      );
    }

    return () => {
      dispatch(removeLayer(COUNTY_BROADBAND_LAYER_ID));
      // dispatch(removeSource(currentCountyBroadbandSource.id));
    };
  }, [year, dispatch, zoomLevel]);

  useEffect(() => {
    let currentStateBroadbandSource = stateBroadbandSource(year);
    let currentStateSocioeconomicSource = stateSocioeconomicSource(
      year,
      socioeconomicFactor
    );
    let currentCountySocioeconomicSource = countySocioeconomicSource(
      year,
      socioeconomicFactor
    );
    let currentTractSocioeconomicSource = tractSocioeconomicSource(
      year,
      socioeconomicFactor
    );
    let featuresSource = socioeconomicFeaturesSource();

    if (features === null) {
      // Available features (filter options).
      executeSQL({
        credentials: credentials,
        query: featuresSource.data,
        connection: featuresSource.connection,
      }).then((result) => {
        let categoryNames = [...new Set(result.map((obj) => obj.category))];

        let categories = categoryNames
          .map((categoryName) => {
            return {
              category: categoryName,
              features: result
                .filter((feature) => categoryName === feature.category)
                .map((feature) => {
                  return { id: feature.feature, label: feature.labels };
                }),
            };
          })
          .sort((a, b) => {
            if (a.category < b.category) {
              return -1;
            }
            if (a.category > b.category) {
              return 1;
            }
            return 0;
          });

        setFeatures(categories);
      });
    }

    if (features === null) {
      return;
    }

    let socioeconomicFactorLabel = findFeatureLabel(features, socioeconomicFactor);

    // State broadband layer.

    dispatch(addSource(currentStateBroadbandSource));
    dispatch(
      addLayer({
        id: STATE_BROADBAND_LAYER_ID,
        source: currentStateBroadbandSource.id,
        layerAttributes: {
          year,
        },
      })
    );

    // State socioeconomic factor source.
    if (isSocioeconomicFactorsLayerActive === true) {
      dispatch(addSource(currentStateSocioeconomicSource));
      executeSQL({
        credentials: credentials,
        query: currentStateSocioeconomicSource.data,
        queryParameters: currentStateSocioeconomicSource.queryParameters,
        connection: currentStateSocioeconomicSource.connection,
      })
        .then((result) => {
          dispatch(
            addLayer({
              id: STATE_SOCIOECONOMIC_LAYER_ID,
              source: currentStateSocioeconomicSource.id,
              layerAttributes: {
                year,
                socioeconomicFactor,
                socioeconomicFactorLabel,
                data: result,
              },
            })
          );
        })
        .catch((_e) => {
          // toast.error(`Error: Unable to retrieve ${socioeconomicFactor}`);
        });
    }

    // County socioeconomic factor layer.
    if (isSocioeconomicFactorsLayerActive === true) {
      dispatch(addSource(currentCountySocioeconomicSource));
      executeSQL({
        credentials: credentials,
        query: currentCountySocioeconomicSource.data,
        queryParameters: currentCountySocioeconomicSource.queryParameters,
        connection: currentCountySocioeconomicSource.connection,
      })
        .then((result) => {
          dispatch(
            addLayer({
              id: COUNTY_SOCIOECONOMIC_LAYER_ID,
              source: currentCountySocioeconomicSource.id,
              layerAttributes: {
                year,
                socioeconomicFactor,
                socioeconomicFactorLabel,
                data: result,
              },
            })
          );
        })
        .catch((_e) => {
          // toast.error(`Error: Unable to retrieve ${socioeconomicFactor}`);
        });
    }

    // Tract socioeconomic factor layer.
    if (isSocioeconomicFactorsLayerActive === true) {
      dispatch(addSource(currentTractSocioeconomicSource));
      executeSQL({
        credentials: credentials,
        query: currentTractSocioeconomicSource.data,
        queryParameters: currentTractSocioeconomicSource.queryParameters,
        connection: currentTractSocioeconomicSource.connection,
      })
        .then((result) => {
          dispatch(
            addLayer({
              id: TRACT_SOCIOECONOMIC_LAYER_ID,
              source: currentTractSocioeconomicSource.id,
              layerAttributes: {
                year,
                socioeconomicFactor,
                socioeconomicFactorLabel,
                data: result,
              },
            })
          );
        })
        .catch((_e) => {
          // toast.error(`Error: Unable to retrieve ${socioeconomicFactor}`);
        });
    }

    if (isLayerVisible(LAYER_TYPE.STATE, zoomLevel) === false) {
      dispatch(removeLayer(STATE_BROADBAND_LAYER_ID));
      dispatch(removeLayer(STATE_SOCIOECONOMIC_LAYER_ID));
      // dispatch(removeSource(currentStateBroadbandSource.id));
      // dispatch(removeSource(currentStateSocioeconomicSource.id));
    }

    if (isLayerVisible(LAYER_TYPE.COUNTY, zoomLevel) === false) {
      dispatch(removeLayer(COUNTY_SOCIOECONOMIC_LAYER_ID));
      // dispatch(removeSource(currentCountySocioeconomicSource.id));
    }

    if (isLayerVisible(LAYER_TYPE.TRACT, zoomLevel) === false) {
      dispatch(removeLayer(TRACT_SOCIOECONOMIC_LAYER_ID));
      // dispatch(removeSource(currentTractSocioeconomicSource.id));
    }

    return () => {
      dispatch(removeLayer(STATE_BROADBAND_LAYER_ID));
      dispatch(removeLayer(STATE_SOCIOECONOMIC_LAYER_ID));
      dispatch(removeLayer(COUNTY_SOCIOECONOMIC_LAYER_ID));
      dispatch(removeLayer(TRACT_SOCIOECONOMIC_LAYER_ID));
      // dispatch(removeSource(currentStateBroadbandSource.id));
      // dispatch(removeSource(currentStateSocioeconomicSource.id));
      // dispatch(removeSource(currentCountySocioeconomicSource.id));
      // dispatch(removeSource(currentTractSocioeconomicSource.id));
    };
  }, [
    dispatch,
    year,
    socioeconomicFactor,
    credentials,
    isSocioeconomicFactorsLayerActive,
    zoomLevel,
    features,
  ]);

  // Retieve state visualizations data to display in the state modal chart.
  useEffect(() => {
    let activeState = searchParams.get('active-state') || null;

    if (activeState === null) {
      return;
    }

    let stateSocioeconomicYearSource = stateVisualizationsSource(
      socioeconomicFactor,
      activeState,
      'year'
    );

    executeSQL({
      credentials: credentials,
      query: stateSocioeconomicYearSource.data,
      queryParameters: stateSocioeconomicYearSource.queryParameters,
      connection: stateSocioeconomicYearSource.connection,
    }).then((result) => {
      setStateSocioeconomicYearsData(result);
    });
  }, [credentials, features, searchParams, socioeconomicFactor, year]);

  // const [previousActiveCounty, setPreviousActiveCounty] = useState(null);

  // Retieve county visualizations data to display in the county modal chart.
  useEffect(() => {
    let activeCounty = searchParams.get('active-county') || null;

    if (activeCounty === null) {
      return;
    }

    let countySocioeconomicYearSource = countyVisualizationsSource(
      socioeconomicFactor,
      activeCounty,
      'year'
    );

    executeSQL({
      credentials: credentials,
      query: countySocioeconomicYearSource.data,
      queryParameters: countySocioeconomicYearSource.queryParameters,
      connection: countySocioeconomicYearSource.connection,
    }).then((result) => {
      setCountySocioeconomicYearsData(result);
    });

    // setPreviousActiveCounty(activeCounty);
  }, [credentials, features, searchParams, socioeconomicFactor, year]);

  const [previousActiveTract, setPreviousActiveTract] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);

  // Retieve tract visualizations data to display in the tract modal chart.
  useEffect(() => {
    let activeTract = searchParams.get('active-tract') || null;

    if (activeTract === null) {
      return;
    }

    // if (
    //   socioeconomicFactor === previousSocioeconomicFactor &&
    //   activeTract === previousActiveTract
    // ) {
    //   return;
    // }

    let tractSocioeconomicBroadbandSource = tractVisualizationsSource(
      socioeconomicFactor,
      activeTract,
      'broadband'
    );

    executeSQL({
      credentials: credentials,
      query: tractSocioeconomicBroadbandSource.data,
      queryParameters: tractSocioeconomicBroadbandSource.queryParameters,
      connection: tractSocioeconomicBroadbandSource.connection,
    }).then((result) => {
      setTractSocioeconomicBroadbandData(result);
    });

    let currentTractPredictionsSource = tractPredictionsSource(
      socioeconomicFactor,
      activeTract
    );

    executeSQL({
      credentials: credentials,
      query: currentTractPredictionsSource.data,
      queryParameters: currentTractPredictionsSource.queryParameters,
      connection: currentTractPredictionsSource.connection,
    }).then((result) => {
      setTractSocioeconomicBroadbandPredictionsData(result);
    });

    let tractSocioeconomicYearSource = tractVisualizationsSource(
      socioeconomicFactor,
      activeTract,
      'year'
    );

    executeSQL({
      credentials: credentials,
      query: tractSocioeconomicYearSource.data,
      queryParameters: tractSocioeconomicYearSource.queryParameters,
      connection: tractSocioeconomicYearSource.connection,
    }).then((result) => {
      setTractSocioeconomicYearsData(result);
    });

    setPreviousActiveTract(activeTract);
    setPreviousSocioeconomicFactor(socioeconomicFactor);
  }, [
    credentials,
    features,
    previousActiveTract,
    previousSocioeconomicFactor,
    searchParams,
    socioeconomicFactor,
    year,
  ]);

  // Retieve tract A and B data to display in the compare similar tracts panel.
  useEffect(() => {
    let activeTract = searchParams.get('active-tract') || null;
    let compareSimilarTract = searchParams.get('active-compare-similar-tract') || null;

    if (activeTract !== null) {
      let tractSocioeconomicBroadbandSourceA = tractVisualizationsSource(
        socioeconomicFactor,
        activeTract,
        'broadband'
      );

      executeSQL({
        credentials: credentials,
        query: tractSocioeconomicBroadbandSourceA.data,
        queryParameters: tractSocioeconomicBroadbandSourceA.queryParameters,
        connection: tractSocioeconomicBroadbandSourceA.connection,
      }).then((result) => {
        setTractSocioeconomicBroadbandDataCompareSimilarTractA(result);
      });

      let currentTractPredictionsSourceA = tractPredictionsSource(
        socioeconomicFactor,
        activeTract
      );

      executeSQL({
        credentials: credentials,
        query: currentTractPredictionsSourceA.data,
        queryParameters: currentTractPredictionsSourceA.queryParameters,
        connection: currentTractPredictionsSourceA.connection,
      }).then((result) => {
        setTractSocioeconomicBroadbandPredictionsDataCompareSimilarTractA(result);
      });
    }

    if (compareSimilarTract !== null) {
      let tractSocioeconomicBroadbandSourceB = tractVisualizationsSource(
        socioeconomicFactor,
        compareSimilarTract,
        'broadband'
      );

      executeSQL({
        credentials: credentials,
        query: tractSocioeconomicBroadbandSourceB.data,
        queryParameters: tractSocioeconomicBroadbandSourceB.queryParameters,
        connection: tractSocioeconomicBroadbandSourceB.connection,
      }).then((result) => {
        setTractSocioeconomicBroadbandDataCompareSimilarTractB(result);
      });

      let currentTractPredictionsSourceB = tractPredictionsSource(
        socioeconomicFactor,
        compareSimilarTract
      );

      executeSQL({
        credentials: credentials,
        query: currentTractPredictionsSourceB.data,
        queryParameters: currentTractPredictionsSourceB.queryParameters,
        connection: currentTractPredictionsSourceB.connection,
      }).then((result) => {
        setTractSocioeconomicBroadbandPredictionsDataCompareSimilarTractB(result);
      });
    }

    // let tractSocioeconomicYearSource = tractVisualizationsSource(
    //   socioeconomicFactor,
    //   compareSimilarTract,
    //   'year'
    // );

    // executeSQL({
    //   credentials: credentials,
    //   query: tractSocioeconomicYearSource.data,
    //   queryParameters: tractSocioeconomicYearSource.queryParameters,
    //   connection: tractSocioeconomicYearSource.connection,
    // }).then((result) => {
    //   setTractSocioeconomicYearsData(result);
    // });
  }, [credentials, features, searchParams, socioeconomicFactor, year]);

  // Retrieve similar tracts to display in the similar tracts side panel.
  useEffect(() => {
    let activeTract = searchParams.get('active-tract');

    if (activeTract === null) {
      return;
    }

    if (
      searchParams.get('active-panel') === null ||
      searchParams.get('active-panel') === 'visualization' ||
      searchParams.get('active-panel') === 'compare-tracts'
    ) {
      setSearchParams((params) => {
        params.delete('similar-tracts');

        return params;
      });

      return;
    }

    let currentSimilarTractsSource = similarTractsSource(activeTract, year);

    executeSQL({
      credentials: credentials,
      query: currentSimilarTractsSource.data,
      queryParameters: currentSimilarTractsSource.queryParameters,
      connection: currentSimilarTractsSource.connection,
    }).then((result) => {
      setSimilarTracts(result);

      if (result !== null) {
        setSearchParams((params) => {
          params.set(
            'similar-tracts',
            result?.map((tract) => tract.tractcode)?.join(',')
          );

          return params;
        });
      }
    });
  }, [
    credentials,
    dispatch,
    features,
    searchParams,
    setSearchParams,
    socioeconomicFactor,
    year,
  ]);

  let unsetActiveTract = function () {
    setSearchParams((params) => {
      params.delete('active-tract');
      params.delete('active-panel');

      return params;
    });

    setActiveTract(null);
  };

  let unsetActiveCounty = function () {
    setSearchParams((params) => {
      params.delete('active-county');
      params.delete('active-panel');

      return params;
    });

    setActiveCounty(null);
  };

  let unsetActiveState = function () {
    setSearchParams((params) => {
      params.delete('active-state');
      params.delete('active-panel');

      return params;
    });

    setActiveState(null);
  };

  let unsetSimilarTractsPanel = function () {
    setSearchParams((params) => {
      params.delete('similar-tracts');
      params.set('active-panel', TRACT_PANEL_TYPES.VISUALIZATION);

      return params;
    });
  };

  let unsetCompareSimilarTractsPanel = function () {
    setSearchParams((params) => {
      params.delete('active-compare-similar-tract');
      params.set('active-panel', TRACT_PANEL_TYPES.SIMILAR_TRACTS);

      return params;
    });
  };

  let unsetCompareTractsPanel = function () {
    setSearchParams((params) => {
      params.delete('active-compare-tract');
      params.set('active-panel', TRACT_PANEL_TYPES.VISUALIZATION);

      return params;
    });
  };

  let unsetCompareCountiesPanel = function () {
    setSearchParams((params) => {
      params.delete('active-compare-county');
      params.set('active-panel', COUNTY_PANEL_TYPES.VISUALIZATION);

      return params;
    });
  };

  let unsetCompareStatesPanel = function () {
    setSearchParams((params) => {
      params.delete('active-compare-state');
      params.set('active-panel', STATE_PANEL_TYPES.VISUALIZATION);

      return params;
    });
  };

  let handleShowSimilarTractsClick = function () {
    // setSimilarTractsPanelIsActive(true);
    setSearchParams((params) => {
      params.set('active-panel', TRACT_PANEL_TYPES.SIMILAR_TRACTS);

      return params;
    });

    if (similarTracts !== null) {
      setSearchParams((params) => {
        params.set(
          'similar-tracts',
          similarTracts?.map((tract) => tract.tractcode)?.join(',')
        );

        return params;
      });
    }
  };

  let handleShowCompareSimilarTractsClick = function (tract) {
    setSearchParams((params) => {
      params.set('active-panel', TRACT_PANEL_TYPES.COMPARE_SIMILAR_TRACTS);
      params.set('active-compare-similar-tract', tract?.tractcode);

      return params;
    });
  };

  let panelContent = searchParams.get('active-panel');

  const [prevActiveTractCode, setPrevActiveTractCode] = useState(null);
  const [prevActiveYear, setPrevActiveYear] = useState(null);

  // Retrieve active tract based on query param change.
  useEffect(() => {
    let activeTractCode = searchParams.get('active-tract');

    if (
      activeTractCode !== null &&
      (activeTractCode !== prevActiveTractCode || year !== prevActiveYear)
    ) {
      const currentTractsByTractcodesSource = tractsByTractcodesSource(
        [searchParams.get('active-tract')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentTractsByTractcodesSource.data,
        queryParameters: currentTractsByTractcodesSource.queryParameters,
        connection: currentTractsByTractcodesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveTract(result[0]);
          } else {
            setActiveTract({ tractcode: searchParams.get('active-tract') });

            toast.error(
              `Warning: No broadband data available for tract ${searchParams.get(
                'active-tract'
              )} in ${searchParams.get('year')}`
            );
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-tract')}`
          );
        });
    }

    setPrevActiveTractCode(activeTractCode);
    setPrevActiveYear(year);
  }, [
    credentials,
    searchParams,
    setActiveTract,
    year,
    prevActiveTractCode,
    setPrevActiveTractCode,
    prevActiveYear,
  ]);

  const [prevActiveCountyId, setPrevActiveCountyId] = useState(null);

  // Retrieve active county based on query param change.
  useEffect(() => {
    let activeCountyId = searchParams.get('active-county');

    if (
      activeCountyId !== null &&
      (activeCountyId !== prevActiveCountyId || year !== prevActiveYear)
    ) {
      const currentCountiesByCountyIdsSource = countiesByCountyIdsSource(
        [searchParams.get('active-county')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentCountiesByCountyIdsSource.data,
        queryParameters: currentCountiesByCountyIdsSource.queryParameters,
        connection: currentCountiesByCountyIdsSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveCounty(result[0]);
          } else {
            const [countyname, statename] = searchParams.get('active-county').split(', ');

            setActiveCounty({ countyname, statename });

            toast.error(
              `Warning: No broadband data available for ${searchParams.get(
                'active-county'
              )} in ${searchParams.get('year')}`
            );
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve county ${searchParams.get('active-county')}`
          );
        });
    }

    setPrevActiveCountyId(activeCountyId);
    setPrevActiveYear(year);
  }, [
    credentials,
    searchParams,
    setActiveCounty,
    year,
    prevActiveCountyId,
    setPrevActiveCountyId,
    prevActiveYear,
  ]);

  const [prevActiveStateName, setPrevActiveStateName] = useState(null);

  // Retrieve active state based on query param change.
  useEffect(() => {
    let activeStateName = searchParams.get('active-state');

    if (
      activeStateName !== null &&
      (activeStateName !== prevActiveStateName || year !== prevActiveYear)
    ) {
      const currenctStatesByStatenameSource = statesByStatenamesSource(
        [searchParams.get('active-state')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currenctStatesByStatenameSource.data,
        queryParameters: currenctStatesByStatenameSource.queryParameters,
        connection: currenctStatesByStatenameSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveState(result[0]);
          } else {
            setActiveState({ statename: searchParams.get('active-state') });

            toast.error(
              `Warning: No broadband data available for ${searchParams.get(
                'active-state'
              )} in ${searchParams.get('year')}`
            );
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-state')}`
          );
        });
    }

    setPrevActiveStateName(activeStateName);
    setPrevActiveYear(year);
  }, [
    credentials,
    searchParams,
    setActiveState,
    year,
    prevActiveStateName,
    setPrevActiveStateName,
    prevActiveYear,
  ]);

  const [prevActiveCompareTractCode, setPrevActiveCompareTractCode] = useState(null);

  // Retrieve active compare tract based on query param change.
  useEffect(() => {
    let activeCompareTractCode = searchParams.get('active-compare-tract') || null;

    if (activeCompareTractCode === null) {
      setActiveCompareTract(null);
    }

    if (
      activeCompareTractCode !== null &&
      activeCompareTractCode !== prevActiveCompareTractCode &&
      activeCompareTract === null
    ) {
      const currentTractsByTractcodesSource = tractsByTractcodesSource(
        [searchParams.get('active-compare-tract')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentTractsByTractcodesSource.data,
        queryParameters: currentTractsByTractcodesSource.queryParameters,
        connection: currentTractsByTractcodesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveCompareTract(result[0]);
            setTractsSearchQuery(result[0].tractcode);
          } else {
            setActiveCompareTract(null);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-compare-tract')}`
          );
        });
    }

    setPrevActiveCompareTractCode(activeCompareTractCode);
  }, [
    credentials,
    searchParams,
    year,
    setActiveCompareTract,
    prevActiveCompareTractCode,
    setPrevActiveCompareTractCode,
    activeCompareTract,
  ]);

  const [prevActiveCompareCountyId, setPrevActiveCompareCountyId] = useState(null);

  // Retrieve active compare county based on query param change.
  useEffect(() => {
    let activeCompareCountyId = searchParams.get('active-compare-county') || null;

    if (activeCompareCountyId === null) {
      setActiveCompareCounty(null);
    }

    if (
      activeCompareCountyId !== null &&
      activeCompareCountyId !== prevActiveCompareCountyId &&
      activeCompareCounty === null
    ) {
      const currentCountiesByCountyIdsSource = countiesByCountyIdsSource(
        [searchParams.get('active-compare-county')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentCountiesByCountyIdsSource.data,
        queryParameters: currentCountiesByCountyIdsSource.queryParameters,
        connection: currentCountiesByCountyIdsSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveCompareCounty(result[0]);
            setCountiesSearchQuery(result[0].countyname);
          } else {
            setActiveCompareCounty(null);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve county ${searchParams.get(
              'active-compare-county'
            )}`
          );
        });
    }

    setPrevActiveCompareCountyId(activeCompareCountyId);
  }, [
    credentials,
    searchParams,
    year,
    setActiveCompareCounty,
    prevActiveCompareCountyId,
    setPrevActiveCompareCountyId,
    activeCompareCounty,
  ]);

  const [prevActiveCompareStateName, setPrevActiveCompareStateName] = useState(null);

  // Retrieve active compare state based on query param change.
  useEffect(() => {
    let activeCompareStateName = searchParams.get('active-compare-state') || null;

    if (activeCompareStateName === null) {
      setActiveCompareState(null);
    }

    if (
      activeCompareStateName !== null &&
      activeCompareStateName !== prevActiveCompareStateName &&
      activeCompareState === null
    ) {
      const currentStatesByStatenamesSource = statesByStatenamesSource(
        [searchParams.get('active-compare-state')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentStatesByStatenamesSource.data,
        queryParameters: currentStatesByStatenamesSource.queryParameters,
        connection: currentStatesByStatenamesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveCompareState(result[0]);
            setStatesSearchQuery(result[0].statename);
          } else {
            setActiveCompareState(null);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve state ${searchParams.get('active-compare-state')}`
          );
        });
    }

    setPrevActiveCompareStateName(activeCompareStateName);
  }, [
    credentials,
    searchParams,
    year,
    setActiveCompareState,
    prevActiveCompareStateName,
    setPrevActiveCompareStateName,
    activeCompareState,
  ]);

  const [prevActiveCompareSimilarTractCode, setPrevActiveCompareSimilarTractCode] =
    useState(null);

  // Retrieve active compare similar tract based on query param change.
  useEffect(() => {
    let activeCompareSimilarTractCode =
      searchParams.get('active-compare-similar-tract') || null;

    if (activeCompareSimilarTractCode === null) {
      setActiveCompareSimilarTract(null);
    }

    if (
      activeCompareSimilarTractCode !== null &&
      activeCompareSimilarTractCode !== prevActiveCompareSimilarTractCode &&
      activeCompareSimilarTract === null
    ) {
      const currentTractsByTractcodesSource = tractsByTractcodesSource(
        [searchParams.get('active-compare-similar-tract')],
        year
      );

      executeSQL({
        credentials: credentials,
        query: currentTractsByTractcodesSource.data,
        queryParameters: currentTractsByTractcodesSource.queryParameters,
        connection: currentTractsByTractcodesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            setActiveCompareSimilarTract(result[0]);
          } else {
            setActiveCompareSimilarTract(null);
          }
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-compare-tract')}`
          );
        });
    }

    setPrevActiveCompareSimilarTractCode(activeCompareSimilarTractCode);
  }, [
    credentials,
    searchParams,
    year,
    setActiveCompareSimilarTract,
    prevActiveCompareSimilarTractCode,
    setPrevActiveCompareSimilarTractCode,
    activeCompareSimilarTract,
  ]);

  // Retrieve tract comparison data on active tract change.
  useEffect(() => {
    let activeTractsMetrics = null;
    let activeTractCode = searchParams.get('active-tract');
    let activeCompareTractCode = searchParams.get('active-compare-tract') || '0';

    if (activeTractCode !== null && features !== null) {
      let socioeconomicFactors = features
        .map((item) =>
          item.category === 'overall' ? 'overall_socioeconomic' : item.category
        )
        .filter((item) => item !== 'broadband');
      const currentTractsByTractcodesSource = tractsByTractcodesSource(
        [activeTractCode, activeCompareTractCode],
        year,
        socioeconomicFactors
      );

      executeSQL({
        credentials: credentials,
        query: currentTractsByTractcodesSource.data,
        queryParameters: currentTractsByTractcodesSource.queryParameters,
        connection: currentTractsByTractcodesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            activeTractsMetrics = result;
          } else {
            activeTractsMetrics = [];
          }

          let tractAMetrics = activeTractsMetrics.find(
            (item) => item.tractcode.toString() === activeTractCode
          );
          let tractBMetrics = activeTractsMetrics.find(
            (item) => item.tractcode.toString() === activeCompareTractCode
          );
          let socioeconomicFactorsCategories = socioeconomicFactors.filter(
            (item) => item !== 'overall_socioeconomic'
          );

          setCompareTractsItems([
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Broadband score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>{tractAMetrics?.broadband.toFixed(1)}</div>
                    {tractBMetrics?.broadband && (
                      <div className='flex justify-between'>
                        {tractBMetrics?.broadband.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            tractBMetrics?.broadband.toFixed(1) -
                            tractAMetrics?.broadband.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: <div></div>,
            },
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Socioeconomic score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>
                      {tractAMetrics?.overall_socioeconomic.toFixed(1)}
                    </div>
                    {tractBMetrics?.overall_socioeconomic && (
                      <div className='flex justify-between'>
                        {tractBMetrics?.overall_socioeconomic.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            tractBMetrics?.overall_socioeconomic.toFixed(1) -
                            tractAMetrics?.overall_socioeconomic.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: (
                <div className='flex flex-col gap-2.5 -ml-[4px] pr-[16px]'>
                  {socioeconomicFactorsCategories.map((item, index) => {
                    return (
                      <div
                        className='flex flex-col pb-2 text-xs border-b border-blue-100'
                        key={index}
                      >
                        <span className='font-normal text-blue-300'>{item}</span>
                        <div className='grid w-full grid-cols-2 text-base text-blue-500'>
                          <div className='text-xs'>
                            {tractAMetrics?.[item].toFixed(1)}
                          </div>
                          {tractBMetrics?.[item] !== undefined && (
                            <div className='flex justify-between text-xs pl-2.5'>
                              {tractBMetrics?.[item].toFixed(1)}{' '}
                              <ScoreIndicator
                                variant='small'
                                value={(
                                  tractBMetrics?.[item].toFixed(1) -
                                  tractAMetrics?.[item].toFixed(1)
                                ).toFixed(1)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            },
          ]);
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve tract ${searchParams.get('active-tract')}`
          );
        });
    }

    setPrevActiveTractCode(activeTractCode);
  }, [
    credentials,
    searchParams,
    setActiveTract,
    year,
    prevActiveTractCode,
    setPrevActiveTractCode,
    features,
  ]);

  // Retrieve county comparison data on active county change.
  useEffect(() => {
    let activeCountiesMetrics = null;
    let activeCountyId = searchParams.get('active-county');
    let activeCountyIdSplitted = activeCountyId?.split(',');
    let activeStateName = activeCountyIdSplitted?.[1]?.trim() || '';
    let activeCountyName = activeCountyIdSplitted?.[0]?.trim() || '';
    let activeCompareCountyId = searchParams.get('active-compare-county') || '0';
    let activeCompareCountyIdSplitted = activeCompareCountyId?.split(',');
    let activeCompareStateName = activeCompareCountyIdSplitted?.[1]?.trim() || '';
    let activeCompareCountyName = activeCompareCountyIdSplitted?.[0]?.trim() || '';

    if (activeCountyId !== null && features !== null) {
      let socioeconomicFactors = features
        .map((item) =>
          item.category === 'overall' ? 'overall_socioeconomic' : item.category
        )
        .filter((item) => item !== 'broadband');
      const currentCountiesByStatenamesSource = countiesByCountyIdsSource(
        [activeCountyId, activeCompareCountyId],
        year,
        socioeconomicFactors
      );

      executeSQL({
        credentials: credentials,
        query: currentCountiesByStatenamesSource.data,
        queryParameters: currentCountiesByStatenamesSource.queryParameters,
        connection: currentCountiesByStatenamesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            activeCountiesMetrics = result;
          } else {
            activeCountiesMetrics = [];
          }

          let countyAMetrics = activeCountiesMetrics.find(
            (item) =>
              item.statename === activeStateName && item.countyname === activeCountyName
          );
          let countyBMetrics = activeCountiesMetrics.find(
            (item) =>
              item.statename === activeCompareStateName &&
              item.countyname === activeCompareCountyName
          );

          let socioeconomicFactorsCategories = socioeconomicFactors.filter(
            (item) => item !== 'overall_socioeconomic'
          );

          setCompareCountiesItems([
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Broadband score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>{countyAMetrics?.broadband.toFixed(1)}</div>
                    {countyBMetrics?.broadband && (
                      <div className='flex justify-between'>
                        {countyBMetrics?.broadband.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            countyBMetrics?.broadband.toFixed(1) -
                            countyAMetrics?.broadband.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: <div></div>,
            },
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Socioeconomic score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>
                      {countyAMetrics?.overall_socioeconomic.toFixed(1)}
                    </div>
                    {countyBMetrics?.overall_socioeconomic && (
                      <div className='flex justify-between'>
                        {countyBMetrics?.overall_socioeconomic.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            countyBMetrics?.overall_socioeconomic.toFixed(1) -
                            countyAMetrics?.overall_socioeconomic.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: (
                <div className='flex flex-col gap-2.5 -ml-[4px] pr-[16px]'>
                  {socioeconomicFactorsCategories.map((item, index) => {
                    return (
                      <div
                        className='flex flex-col pb-2 text-xs border-b border-blue-100'
                        key={index}
                      >
                        <span className='font-normal text-blue-300'>{item}</span>
                        <div className='grid w-full grid-cols-2 text-base text-blue-500'>
                          <div className='text-xs'>
                            {countyAMetrics?.[item].toFixed(1)}
                          </div>
                          {countyBMetrics?.[item] !== undefined && (
                            <div className='flex justify-between text-xs pl-2.5'>
                              {countyBMetrics?.[item].toFixed(1)}{' '}
                              <ScoreIndicator
                                variant='small'
                                value={(
                                  countyBMetrics?.[item].toFixed(1) -
                                  countyAMetrics?.[item].toFixed(1)
                                ).toFixed(1)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            },
          ]);
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve county ${searchParams.get('active-county')}`
          );
        });
    }

    setPrevActiveCountyId(activeCountyId);
  }, [
    credentials,
    searchParams,
    setActiveCounty,
    year,
    prevActiveCountyId,
    setPrevActiveCountyId,
    features,
  ]);

  // Retrieve state comparison data on active state change.
  useEffect(() => {
    let activeStatesMetrics = null;
    let activeStateName = searchParams.get('active-state');
    let activeCompareStateName = searchParams.get('active-compare-state') || '0';

    if (activeStateName !== null && features !== null) {
      let socioeconomicFactors = features
        .map((item) =>
          item.category === 'overall' ? 'overall_socioeconomic' : item.category
        )
        .filter((item) => item !== 'broadband');
      const currentStatesByStatenamesSource = statesByStatenamesSource(
        [activeStateName, activeCompareStateName],
        year,
        socioeconomicFactors
      );

      executeSQL({
        credentials: credentials,
        query: currentStatesByStatenamesSource.data,
        queryParameters: currentStatesByStatenamesSource.queryParameters,
        connection: currentStatesByStatenamesSource.connection,
      })
        .then((result) => {
          if (result?.length > 0) {
            activeStatesMetrics = result;
          } else {
            activeStatesMetrics = [];
          }

          let stateAMetrics = activeStatesMetrics.find(
            (item) => item.statename === activeStateName
          );
          let stateBMetrics = activeStatesMetrics.find(
            (item) => item.statename === activeCompareStateName
          );
          let socioeconomicFactorsCategories = socioeconomicFactors.filter(
            (item) => item !== 'overall_socioeconomic'
          );

          setCompareStatesItems([
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Broadband score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>{stateAMetrics?.broadband.toFixed(1)}</div>
                    {stateBMetrics?.broadband && (
                      <div className='flex justify-between'>
                        {stateBMetrics?.broadband.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            stateBMetrics?.broadband.toFixed(1) -
                            stateAMetrics?.broadband.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: <div></div>,
            },
            {
              title: (
                <div className='flex flex-col w-full pr-[7px]'>
                  <span className='mt-px text-sm text-blue-300'>Socioeconomic score</span>
                  <div className='grid w-full h-6 grid-cols-2 gap-2 text-base text-blue-500'>
                    <div className=''>
                      {stateAMetrics?.overall_socioeconomic.toFixed(1)}
                    </div>
                    {stateBMetrics?.overall_socioeconomic && (
                      <div className='flex justify-between'>
                        {stateBMetrics?.overall_socioeconomic.toFixed(1)}{' '}
                        <ScoreIndicator
                          value={(
                            stateBMetrics?.overall_socioeconomic.toFixed(1) -
                            stateAMetrics?.overall_socioeconomic.toFixed(1)
                          ).toFixed(1)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ),
              content: (
                <div className='flex flex-col gap-2.5 -ml-[4px] pr-[16px]'>
                  {socioeconomicFactorsCategories.map((item, index) => {
                    return (
                      <div
                        className='flex flex-col pb-2 text-xs border-b border-blue-100'
                        key={index}
                      >
                        <span className='font-normal text-blue-300'>{item}</span>
                        <div className='grid w-full grid-cols-2 text-base text-blue-500'>
                          <div className='text-xs'>
                            {stateAMetrics?.[item].toFixed(1)}
                          </div>
                          {stateBMetrics?.[item] !== undefined && (
                            <div className='flex justify-between text-xs pl-2.5'>
                              {stateBMetrics?.[item].toFixed(1)}{' '}
                              <ScoreIndicator
                                variant='small'
                                value={(
                                  stateBMetrics?.[item].toFixed(1) -
                                  stateAMetrics?.[item].toFixed(1)
                                ).toFixed(1)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            },
          ]);
        })
        .catch((_e) => {
          toast.error(
            `Error: Unable to retrieve state ${searchParams.get('active-state')}`
          );
        });
    }

    setPrevActiveStateName(activeStateName);
  }, [
    credentials,
    searchParams,
    setActiveState,
    year,
    prevActiveStateName,
    setPrevActiveStateName,
    features,
  ]);

  // let activePanel = searchParams.get('active-panel') || null;
  // let hideFilterBox =
  //   activePanel !== null &&
  //   activePanel !== TRACT_PANEL_TYPES.VISUALIZATION &&
  //   activePanel !== STATE_PANEL_TYPES.VISUALIZATION;

  let hideFilterBox = false;

  return (
    <div className='relative flex flex-1 overflow-hidden'>
      <div className='relative top-0 left-0 z-0 flex-1 w-full'>
        <LazyLoadComponent>
          <MapContainer features={features} socioeconomicFactor={socioeconomicFactor} />
        </LazyLoadComponent>
      </div>
      <div
        className={`absolute z-10 max-h-[calc(100%_-_10.5rem)] mx-10 transition-opacity duration-200 my-8 rounded-xl ${
          hideFilterBox === true ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className='h-full max-h-full w-96'>
          <FilterBox
            year={year}
            setYear={setYear}
            socioeconomicFactor={socioeconomicFactor}
            setSocioeconomicFactor={setSocioeconomicFactor}
            features={features}
            setFeatures={setFeatures}
            isSocioeconomicFactorsLayerActive={isSocioeconomicFactorsLayerActive}
            setIsSocioeconomicFactorsLayerActive={
              toggleSocioeconomicFactorsLayerActiveSearchParam
            }
            handleShowSimilarTractsClick={handleShowSimilarTractsClick}
            broadbandLayers={broadbandLayers}
            activeBroadbandLayer={activeBroadbandLayer}
            setActiveBroadbandLayer={activateBroadbandLayerSearchParam}
          ></FilterBox>
        </div>
      </div>
      <SidePanel isActive={searchParams.get('active-tract') !== null}>
        {(panelContent === TRACT_PANEL_TYPES.VISUALIZATION || panelContent === null) && (
          <TractSidePanel
            backLinkAction={unsetActiveTract}
            features={features}
            activeTract={activeTract}
            socioeconomicFactor={socioeconomicFactor}
            socioeconomicBroadbandData={tractSocioeconomicBroadbandData}
            socioeconomicBroadbandPredictionsData={
              tractSocioeconomicBroadbandPredictionsData
            }
            socioeconomicYearsData={tractSocioeconomicYearsData}
          ></TractSidePanel>
        )}

        {panelContent === TRACT_PANEL_TYPES.SIMILAR_TRACTS && (
          <SimilarTractsSidePanel
            handleSimilarTractClick={handleShowCompareSimilarTractsClick}
            activeTract={activeTract}
            similarTracts={similarTracts}
            backLinkAction={unsetSimilarTractsPanel}
            socioeconomicFactor={socioeconomicFactorParsed}
            socioeconomicYearsChartData={socioeconomicYearsChartData}
            socioeconomicYearsChartLabels={socioeconomicYearsChartLabels}
          ></SimilarTractsSidePanel>
        )}

        {panelContent === TRACT_PANEL_TYPES.COMPARE_SIMILAR_TRACTS && (
          <CompareSimilarTractsSidePanel
            backLinkAction={unsetCompareSimilarTractsPanel}
            features={features}
            activeTract={activeTract}
            activeCompareSimilarTract={activeCompareSimilarTract}
            socioeconomicFactor={socioeconomicFactor}
            socioeconomicBroadbandDataTractA={
              tractSocioeconomicBroadbandDataCompareSimilarTractA
            }
            socioeconomicBroadbandPredictionsDataTractA={
              tractSocioeconomicBroadbandPredictionsDataCompareSimilarTractA
            }
            socioeconomicBroadbandDataTractB={
              tractSocioeconomicBroadbandDataCompareSimilarTractB
            }
            socioeconomicBroadbandPredictionsDataTractB={
              tractSocioeconomicBroadbandPredictionsDataCompareSimilarTractB
            }
            updateSelected={updateSelectedCompareSimilarTract}
          ></CompareSimilarTractsSidePanel>
        )}

        {panelContent === TRACT_PANEL_TYPES.COMPARE_TRACTS && (
          <CompareTractsSidePanel
            items={compareTractsItems}
            activeTract={activeTract}
            backLinkAction={unsetCompareTractsPanel}
            searchQuery={tractsSearchQuery}
            searchResults={searchResultsTracts}
            setSearchQuery={setTractsSearchQuery}
            isSearchResultsLoading={isSearchResultsTractsLoading}
            updateSelected={updateSelectedTract}
            activeCompareTract={activeCompareTract}
          ></CompareTractsSidePanel>
        )}
      </SidePanel>

      <SidePanel isActive={searchParams.get('active-county') !== null}>
        {(panelContent === COUNTY_PANEL_TYPES.VISUALIZATION || panelContent === null) && (
          <CountySidePanel
            backLinkAction={unsetActiveCounty}
            features={features}
            activeCounty={activeCounty}
            socioeconomicFactor={socioeconomicFactor}
            socioeconomicYearsData={countySocioeconomicYearsData}
          ></CountySidePanel>
        )}

        {panelContent === COUNTY_PANEL_TYPES.COMPARE_COUNTIES && (
          <CompareCountiesSidePanel
            items={compareCountiesItems}
            activeCounty={activeCounty}
            backLinkAction={unsetCompareCountiesPanel}
            searchQuery={countiesSearchQuery}
            searchResults={searchResultsCounties}
            setSearchQuery={setCountiesSearchQuery}
            isSearchResultsLoading={isSearchResultsCountiesLoading}
            updateSelected={updateSelectedCounty}
            activeCompareCounty={activeCompareCounty}
          ></CompareCountiesSidePanel>
        )}
      </SidePanel>

      <SidePanel isActive={searchParams.get('active-state') !== null}>
        {(panelContent === STATE_PANEL_TYPES.VISUALIZATION || panelContent === null) && (
          <StateSidePanel
            backLinkAction={unsetActiveState}
            features={features}
            activeState={activeState}
            socioeconomicFactor={socioeconomicFactor}
            socioeconomicYearsData={stateSocioeconomicYearsData}
          ></StateSidePanel>
        )}

        {panelContent === STATE_PANEL_TYPES.COMPARE_STATES && (
          <CompareStatesSidePanel
            items={compareStatesItems}
            activeState={activeState}
            backLinkAction={unsetCompareStatesPanel}
            searchQuery={statesSearchQuery}
            searchResults={searchResultsStates}
            setSearchQuery={setStatesSearchQuery}
            isSearchResultsLoading={isSearchResultsStatesLoading}
            updateSelected={updateSelectedState}
            activeCompareState={activeCompareState}
          ></CompareStatesSidePanel>
        )}
      </SidePanel>
    </div>
  );
}
